import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import equipmentService from '../../services/equipment';
import jobService from '../../services/jobs';
import orchardService from '../../services/orchard';
import HeldEquipmentPanel from '../../components/equipment/HeldEquipmentPanel';
import { EquipmentPhotoThumb } from '../../components/equipment/EquipmentPhotos';
import type { Equipment, EquipmentTypeOption, EquipmentCategory, HeldEquipment } from '../../types/equipment';
import { EQUIPMENT_CATEGORIES, categoryOf, categoryLabel } from '../../types/equipment';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import {
  Tractor, Wrench, AlertTriangle, ClipboardList,
  Plus, MapPin, Gauge, ChevronRight, Filter, Zap, Settings, UserRound, Search, X, CreditCard,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { matchesEquipmentSearch } from '../../utils/equipmentSearch';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusColor(status: Equipment['status']) {
  switch (status) {
    case 'active':       return 'bg-green-100 text-green-700';
    case 'inactive':     return 'bg-neutral-100 text-neutral-500';
    case 'under_repair': return 'bg-red-100 text-red-700';
    case 'retired':      return 'bg-neutral-200 text-neutral-400';
  }
}

function meterDisplay(eq: Equipment) {
  const parts: string[] = [];
  if ((eq.meter_type === 'hours' || eq.meter_type === 'both') && eq.current_hours != null)
    parts.push(`${eq.current_hours.toLocaleString()} hrs`);
  if ((eq.meter_type === 'km' || eq.meter_type === 'both') && eq.current_km != null)
    parts.push(`${eq.current_km.toLocaleString()} km`);
  return parts.join(' · ') || null;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function EquipmentList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasAccess: canCreateEquipment } = useModuleLicense('vehicles', 'creator');

  const [equipment, setEquipment]       = useState<Equipment[]>([]);
  const [types, setTypes]               = useState<EquipmentTypeOption[]>([]);
  const [farms, setFarms]               = useState<Array<{ id: number; name: string }>>([]);
  const [pendingJobsByEq, setPendingJobsByEq] = useState<Map<number, { id: number; name: string }[]>>(new Map());
  const [loading, setLoading]           = useState(true);
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('active');
  const [selectedCategory, setSelectedCategory] = useState<EquipmentCategory | 'all'>('all');
  const [held, setHeld]                 = useState<HeldEquipment[]>([]);
  const [selectedFarmId, setSelectedFarmId] = useState<number | ''>('');
  const [selectedStorage, setSelectedStorage] = useState<string>('');
  // In the URL so Back from a machine returns to the same search.
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const setQuery = (q: string) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (q) next.set('q', q); else next.delete('q');
    return next;
  }, { replace: true });

  useEffect(() => {
    orchardService.getFarms(1, 100).then(res => {
      if (res.success && res.data) setFarms(res.data.farms);
    });
  }, []);

  // Load the caller's pending (assigned-but-not-started) jobs so we can show a
  // badge on equipment they need to operate.
  useEffect(() => {
    if (!user?.id) return;
    jobService.getJobs({ assigned_to: user.id, status: 'assigned', limit: 50 }).then(res => {
      if (!res.success || !res.data) return;
      const byEq = new Map<number, { id: number; name: string }[]>();
      for (const job of res.data.jobs) {
        for (const eq of job.equipment ?? []) {
          if (eq.role !== 'primary') continue;
          const arr = byEq.get(eq.equipment_id) ?? [];
          arr.push({ id: job.id, name: job.name });
          byEq.set(eq.equipment_id, arr);
        }
      }
      setPendingJobsByEq(byEq);
    }).catch(() => { /* ignore */ });
  }, [user?.id]);

  // What the caller has been issued. Needs no equipment seat — an employee who
  // signed for a trimmer should find it here without managing the register.
  useEffect(() => {
    equipmentService.getMine()
      .then(res => { if (res.success && res.data) setHeld(res.data); })
      .catch(() => { /* the panel simply does not show */ });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eqRes, typesRes] = await Promise.all([
        equipmentService.list({
          status:  selectedStatus || undefined,
          farm_id: selectedFarmId || undefined,
        }),
        equipmentService.getTypes(),
      ]);
      if (eqRes.success && eqRes.data)      setEquipment(eqRes.data);
      if (typesRes.success && typesRes.data) setTypes(typesRes.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [selectedStatus, selectedFarmId]);

  useEffect(() => { load(); }, [load]);

  // Unique storage locations from the full fetched set
  const storageOptions = [...new Set(
    equipment.map(e => e.location_name).filter((l): l is string => !!l)
  )].sort();

  // Only the categories this fleet actually has — an orchard with no pumps on
  // the register should not be offered a Pumps tab that is always empty.
  const categoriesPresent = EQUIPMENT_CATEGORIES.filter(c => equipment.some(e => categoryOf(e) === c.key));
  const inCategory = equipment.filter(e => selectedCategory === 'all' || categoryOf(e) === selectedCategory);

  // The type picker narrows WITHIN the category tabs rather than repeating
  // them: it offers only the types present under the current tab, and only
  // when there is more than one to choose between. Filtered client-side so
  // picking a type does not shrink the fleet the tabs and options are built from.
  const typesPresent = new Set(inCategory.map(e => e.equipment_type));
  const typeOptions = types.filter(t => typesPresent.has(t.type_key));
  const typeLabels = new Map(types.map(t => [t.type_key, t.label]));
  const displayList = inCategory
    .filter(e => !selectedType || e.equipment_type === selectedType)
    .filter(e => !selectedStorage || e.location_name === selectedStorage)
    .filter(e => matchesEquipmentSearch(e, query, typeLabels.get(e.equipment_type)))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="min-h-screen bg-neutral-50">
      <PageHeader
        title="Equipment"
        subtitle="Vehicles, machinery, pumps and tools"
        icon={<Wrench className="h-6 w-6 text-white" />}
        actionButtons={
          canCreateEquipment ? (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => navigate('/equipment/admin')}>
                <Settings className="h-4 w-4 mr-1" />
                Settings
              </Button>
              <Button size="sm" onClick={() => navigate('/equipment/new')}>
                <Plus className="h-4 w-4 mr-1.5" />
                Add Equipment
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">

        <HeldEquipmentPanel items={held} onOpen={id => navigate(`/equipment/${id}`)} />

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search name, make, model, rego, VIN, who has it…"
            aria-label="Search equipment"
            className="w-full min-w-0 rounded-xl border border-neutral-200 bg-white pl-9 pr-9 py-2.5 text-sm text-neutral-800 focus:outline-none focus:ring-2 focus:ring-green-500 [&::-webkit-search-cancel-button]:hidden"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-neutral-400 hover:text-neutral-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Category */}
        {categoriesPresent.length > 1 && (
          <div className="flex flex-wrap gap-1.5 text-sm" role="tablist" aria-label="Equipment category">
            {[{ key: 'all' as const, label: 'All' }, ...categoriesPresent].map(c => (
              <button
                key={c.key}
                role="tab"
                aria-selected={selectedCategory === c.key}
                onClick={() => { setSelectedCategory(c.key); setSelectedType(''); }}
                className={cn('px-3 py-1.5 rounded-lg border font-medium transition-colors',
                  selectedCategory === c.key
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-white text-neutral-600 border-neutral-200 hover:bg-neutral-50')}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          {/* Status filter */}
          <div className="flex items-center gap-1.5 text-sm">
            <Filter className="h-3.5 w-3.5 text-neutral-400" />
            {(['active', 'inactive', 'under_repair', '']).map(s => (
              <button
                key={s}
                onClick={() => setSelectedStatus(s)}
                className={cn('px-2.5 py-1 rounded-full border font-medium transition-colors',
                  selectedStatus === s
                    ? 'bg-neutral-800 text-white border-neutral-800'
                    : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400')}
              >
                {s === '' ? 'All' : s === 'under_repair' ? 'Under Repair' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          {/* Type filter */}
          {typeOptions.length > 1 && (
            <select
              value={selectedType}
              onChange={e => setSelectedType(e.target.value)}
              aria-label="Equipment type"
              className="text-sm border border-neutral-200 rounded-lg px-2.5 py-1.5 bg-white text-neutral-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">
                {selectedCategory === 'all' ? 'All types' : `All ${categoryLabel(selectedCategory).toLowerCase()}`}
              </option>
              {typeOptions.map(t => (
                <option key={t.type_key} value={t.type_key}>{t.label}</option>
              ))}
            </select>
          )}

          {/* Farm filter */}
          {farms.length > 0 && (
            <select
              value={selectedFarmId}
              onChange={e => {
                setSelectedFarmId(e.target.value ? Number(e.target.value) : '');
                setSelectedStorage('');
              }}
              className="text-sm border border-neutral-200 rounded-lg px-2.5 py-1.5 bg-white text-neutral-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">All farms</option>
              {farms.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          )}

          {/* Storage filter */}
          {storageOptions.length > 0 && (
            <select
              value={selectedStorage}
              onChange={e => setSelectedStorage(e.target.value)}
              className="text-sm border border-neutral-200 rounded-lg px-2.5 py-1.5 bg-white text-neutral-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">All storage</option>
              {storageOptions.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2].map(i => (
              <div key={i} className="h-20 rounded-xl bg-white border border-neutral-100 animate-pulse" />
            ))}
          </div>
        ) : displayList.length === 0 && query.trim() ? (
          <Card className="text-center py-10">
            <CardContent className="space-y-3">
              <Search className="h-8 w-8 text-neutral-300 mx-auto" />
              <p className="text-neutral-600 break-words">No equipment matches &ldquo;{query.trim()}&rdquo;</p>
              {(selectedStatus || selectedCategory !== 'all' || selectedType || selectedFarmId || selectedStorage) && (
                <>
                  <p className="text-xs text-neutral-500">The filters above are also narrowing the list.</p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedStatus('');
                      setSelectedCategory('all');
                      setSelectedType('');
                      setSelectedFarmId('');
                      setSelectedStorage('');
                    }}
                  >
                    Search all equipment
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        ) : displayList.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Tractor className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
              <p className="text-neutral-500">No equipment found</p>
              {canCreateEquipment && (
                <Button size="sm" className="mt-4" onClick={() => navigate('/equipment/new')}>
                  Add your first equipment
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {displayList.map(eq => {
              const pending = pendingJobsByEq.get(eq.id) ?? [];
              return (
                <EquipmentRow
                  key={eq.id}
                  equipment={eq}
                  isMine={eq.assigned_user_id != null && eq.assigned_user_id === user?.id}
                  pendingJobs={pending}
                  onStart={pending.length === 1
                    ? () => navigate(`/equipment/${eq.id}/pre-start?job_id=${pending[0].id}`)
                    : pending.length > 1
                      ? () => navigate(`/equipment/${eq.id}/pre-start`)
                      : null}
                  onClick={() => navigate(`/equipment/${eq.id}`)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function EquipmentRow({
  equipment: eq,
  isMine,
  pendingJobs,
  onStart,
  onClick,
}: {
  equipment: Equipment;
  isMine: boolean;
  pendingJobs: { id: number; name: string }[];
  onStart: (() => void) | null;
  onClick: () => void;
}) {
  const orgTz = useOrgTimezone();
  const meter = meterDisplay(eq);
  const hasActiveSession = !!eq.active_session_id;
  const maintenanceDue = (eq.maintenance_due_count ?? 0) > 0;

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      className="w-full text-left bg-white border border-neutral-100 rounded-xl px-4 py-3 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-3 cursor-pointer"
    >
      {/* Status indicator */}
      <div className={cn(
        'w-2 h-2 rounded-full flex-shrink-0 mt-0.5',
        eq.status === 'active' && !hasActiveSession ? 'bg-green-400' :
        eq.status === 'active' && hasActiveSession  ? 'bg-blue-500 animate-pulse' :
        eq.status === 'under_repair' ? 'bg-red-500' : 'bg-neutral-300'
      )} />

      <EquipmentPhotoThumb photoId={eq.primary_photo_id} alt={eq.name} className="h-12 w-12 rounded-lg" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-neutral-800 truncate">{eq.name}</span>
          <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', statusColor(eq.status))}>
            {eq.status === 'under_repair' ? 'Under Repair' : eq.status.charAt(0).toUpperCase() + eq.status.slice(1)}
          </span>
          {hasActiveSession && (
            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700 flex items-center gap-1">
              <Zap className="h-3 w-3" />
              In use — {eq.active_session_operator}
            </span>
          )}
          {maintenanceDue && (
            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {eq.maintenance_due_count} service{eq.maintenance_due_count === 1 ? '' : 's'} due
            </span>
          )}
          {eq.assigned_user_id != null && (
            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-violet-100 text-violet-700 flex items-center gap-1 min-w-0">
              <UserRound className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">
                {isMine ? 'Issued to you' : `With ${eq.assigned_user_name ?? 'someone'}`}
                {eq.assigned_due_back_on ? ` · back ${formatLocalDateInTz(eq.assigned_due_back_on, orgTz)}` : ''}
              </span>
            </span>
          )}
          {pendingJobs.length > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-indigo-100 text-indigo-700 flex items-center gap-1">
              <ClipboardList className="h-3 w-3" />
              {pendingJobs.length === 1
                ? pendingJobs[0].name
                : `${pendingJobs.length} jobs waiting`}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-xs text-neutral-500">
          {eq.make && <span>{eq.make}{eq.model ? ` ${eq.model}` : ''}</span>}
          {eq.rego_number && (
            <span className="flex items-center gap-0.5">
              <CreditCard className="h-3 w-3" />
              {eq.rego_number}{eq.rego_state ? ` ${eq.rego_state}` : ''}
            </span>
          )}
          {eq.farm_name && (
            <span className="flex items-center gap-0.5">
              <MapPin className="h-3 w-3" />
              {eq.farm_name}
            </span>
          )}
          {meter && (
            <span className="flex items-center gap-0.5">
              <Gauge className="h-3 w-3" />
              {meter}
            </span>
          )}
          {(eq.current_implements?.length ?? 0) > 0 && (
            <span className="flex items-center gap-0.5">
              <Wrench className="h-3 w-3" />
              {eq.current_implements!.map(i => i.name).join(', ')}
            </span>
          )}
        </div>
      </div>

      {onStart && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onStart(); }}
          className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1"
        >
          Start
          <ChevronRight className="h-3 w-3" />
        </button>
      )}

      <ChevronRight className="h-4 w-4 text-neutral-400 flex-shrink-0" />
    </div>
  );
}
